import { Group } from '__types__/v1/models/Group';
import { ApiClient } from 'lib/api/ApiClient.types';
import { GROUP_ACTIONS } from 'store/v1/groups/groups.constants';
import { ReduxProxy } from 'v5/api/_common/reduxProxy';
import { WithServices } from 'v5/platform/ts/services';
import { WithMessageService } from 'v5/services/internal/MessageService/MessageService';
import { ResourceGroupsMutationService } from './ResourceGroupsMutationService';
import { saveResourceGroups } from './saveResourceGroups';

/** Reuse the existing group store so edits also reach resource filters/pills. */
export function createResourceGroupsMutationService({
  client,
  reduxProxy,
  services,
  resolveGroup
}: WithServices<[WithMessageService]> & {
  client: ApiClient;
  reduxProxy: Pick<ReduxProxy, 'write'>;
  resolveGroup: (id: number) => Group | undefined;
}): ResourceGroupsMutationService {
  const service: ResourceGroupsMutationService = {
    async create(resourceTypeId, input) {
      const result = await client.post<Group>('/groups/resource_types', {
        token: true,
        data: { ...input, entity_id: resourceTypeId }
      });
      reduxProxy.write.emit({
        type: GROUP_ACTIONS.CREATE_GROUP_SUCCESS,
        result
      });
      return result;
    },
    async update(id, patch) {
      const result = await client.put<Group>(`/groups/${id}`, {
        token: true,
        data: patch
      });
      reduxProxy.write.emit({
        type: GROUP_ACTIONS.UPDATE_GROUP_SUCCESS,
        result
      });
    },
    async archive(id) {
      // DELETE is a soft delete (archived=true); resources are not deleted.
      await client.del(`/groups/${id}`, { token: true });
      // Keep the row available to the Archived tab without reloading the list.
      const group = resolveGroup(id);
      if (group) {
        reduxProxy.write.emit({
          type: GROUP_ACTIONS.UPDATE_GROUP_SUCCESS,
          result: { ...group, archived: true }
        });
      } else {
        await services.messageService.reportSystemEffect.sendRequest(
          {
            scope: [
              {
                entity: 'resourceGroup',
                entityId: id,
                payload: { type: 'update' }
              }
            ]
          },
          3000
        );
      }
    },
    async unarchive(id) {
      const result = await client.put<Group>(`/groups/${id}`, {
        token: true,
        data: { archived: false }
      });
      reduxProxy.write.emit({
        type: GROUP_ACTIONS.UPDATE_GROUP_SUCCESS,
        result
      });
    },
    async reorder(orderedIds) {
      const result = await client.put<Group[]>('/reorder/groups', {
        token: true,
        data: { reorder: orderedIds.map((id, order) => ({ id, order })) }
      });
      reduxProxy.write.emit({
        type: GROUP_ACTIONS.REORDER_GROUPS_SUCCESS,
        result
      });
    },
    saveGroups: (resourceTypeId, progress, draft) =>
      saveResourceGroups(service, resourceTypeId, progress, draft)
  };
  return service;
}
